import { useState } from 'react';
import { SaleTransaction, Product, Driver } from '../../domain/entities';
import { ProcessSaleUseCase, LoadTransactionsUseCase } from '../../application/useCases';
import { useApp } from '../contexts';

export const useSaleTransaction = (
    cnpj: string | undefined,
    token: string | null,
    supplierId: number | null
) => {
    const { showError, showConfirm } = useApp();
    const [transaction, setTransaction] = useState<SaleTransaction>(SaleTransaction.createEmpty());
    const [isProcessing, setIsProcessing] = useState(false);

    const updateOdometer = (value: string) => {
        setTransaction(prev => new SaleTransaction(
            prev.vehicle,
            prev.product,
            prev.driver,
            value,
            prev.liters,
            prev.totalValue,
            prev.password,
            prev.balance,
            prev.basePrice,
            prev.authorization,
            prev.document,
            prev.clientReceipt,
            prev.establishmentReceipt,
            prev.transactionId,
            prev.xmlAttached
        ));
    };

    const updateProduct = (product: Product | null) => {
        setTransaction(prev => new SaleTransaction(
            prev.vehicle,
            product,
            prev.driver,
            prev.odometer,
            prev.liters,
            prev.totalValue,
            prev.password,
            null,
            null,
            prev.authorization,
            prev.document,
            prev.clientReceipt,
            prev.establishmentReceipt,
            prev.transactionId,
            prev.xmlAttached
        ));
    };

    const updateLiters = (value: string) => {
        setTransaction(prev => new SaleTransaction(
            prev.vehicle,
            prev.product,
            prev.driver,
            prev.odometer,
            value,
            prev.totalValue,
            prev.password,
            prev.balance,
            prev.basePrice,
            prev.authorization,
            prev.document,
            prev.clientReceipt,
            prev.establishmentReceipt,
            prev.transactionId,
            prev.xmlAttached
        ));
    };

    const updateTotalValue = (value: string) => {
        setTransaction(prev => new SaleTransaction(
            prev.vehicle,
            prev.product,
            prev.driver,
            prev.odometer,
            prev.liters,
            value,
            prev.password,
            prev.balance,
            prev.basePrice,
            prev.authorization,
            prev.document,
            prev.clientReceipt,
            prev.establishmentReceipt,
            prev.transactionId,
            prev.xmlAttached
        ));
    };

    const updateDriver = (driver: Driver) => {
        setTransaction(prev => new SaleTransaction(
            prev.vehicle,
            prev.product,
            driver,
            prev.odometer,
            prev.liters,
            prev.totalValue,
            prev.password,
            prev.balance,
            prev.basePrice,
            prev.authorization,
            prev.document,
            prev.clientReceipt,
            prev.establishmentReceipt,
            prev.transactionId,
            prev.xmlAttached
        ));
    };

    const updatePassword = (value: string) => {
        setTransaction(prev => new SaleTransaction(
            prev.vehicle,
            prev.product,
            prev.driver,
            prev.odometer,
            prev.liters,
            prev.totalValue,
            value,
            prev.balance,
            prev.basePrice,
            prev.authorization,
            prev.document,
            prev.clientReceipt,
            prev.establishmentReceipt,
            prev.transactionId,
            prev.xmlAttached
        ));
    };

    const processSale = async (): Promise<boolean> => {
        if (!cnpj || !token) {
            showError('CNPJ ou token não encontrado');
            return false;
        }

        if (!transaction.isValid) {
            showError('Por favor, preencha todos os campos obrigatórios');
            return false;
        }

        if (isProcessing) return false;

        try {
            setIsProcessing(true);
            const useCase = new ProcessSaleUseCase();
            const result = await useCase.execute(transaction, cnpj, token);

            let transactionId = transaction.transactionId;
            if (!transactionId && supplierId && token) {
                try {
                    const loadTransactionsUseCase = new LoadTransactionsUseCase();
                    const transactions = await loadTransactionsUseCase.execute(supplierId, token);
                    if (transactions.length > 0) {
                        transactionId = transactions[0].id;
                    }
                } catch (error) {
                    console.error('Error loading transaction ID:', error);
                }
            }

            setTransaction(prev => new SaleTransaction(
                prev.vehicle,
                prev.product,
                prev.driver,
                prev.odometer,
                prev.liters,
                prev.totalValue,
                prev.password,
                prev.balance,
                prev.basePrice,
                result.authorization,
                result.document,
                result.clientReceipt,
                result.establishmentReceipt,
                transactionId,
                prev.xmlAttached
            ));

            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro ao processar venda';
            showError(message);
            return false;
        } finally {
            setIsProcessing(false);
        }
    };

    const submitSale = async (): Promise<boolean> => {
        if (transaction.basePrice && !transaction.isPriceMatch) {
            return new Promise((resolve) => {
                showConfirm(
                    'O valor por litro informado não corresponde ao preço base praticado. Deseja continuar com o processamento da venda?',
                    async () => {
                        const success = await processSale();
                        resolve(success);
                    }
                );
            });
        }

        return await processSale();
    };

    const resetForNewSale = () => {
        setTransaction(prev => prev.resetSaleData());
    };

    const resetAll = () => {
        setTransaction(SaleTransaction.createEmpty());
    };

    return {
        transaction,
        setTransaction,
        updateOdometer,
        updateProduct,
        updateLiters,
        updateTotalValue,
        updateDriver,
        updatePassword,
        submitSale,
        resetForNewSale,
        resetAll,
        isProcessing,
    };
};

